import { useCallback, useEffect } from 'react';

import { isApplicationModeInfinity } from 'src/data/system';
import type { VoidFunction } from 'src/lib/types';
// @ts-expect-error - useNotificationsCenterActions is not typed yet
import { useNotificationsCenterActions } from 'src/store/notifications';

import { areCustomerNotifications } from '../lib';

const NOTIFICATIONS_POOLING_INTERVAL = 60 * 1000; // 1 min

interface Params {
  customerId: string | null;
  // Called when a notification says the customer changed. A reviewer has no customer to refresh.
  onCustomerNotifications?: VoidFunction;
}

export const useMaintainNotifications = ({ customerId, onCustomerNotifications }: Params) => {
  const notificationsCenterActions = useNotificationsCenterActions();

  // Update notifications each `NOTIFICATIONS_POOLING_INTERVAL`
  const pullNotifications = useCallback(async () => {
    if (!customerId) {
      return;
    }

    const notifications = await notificationsCenterActions.queryNotifications(customerId);

    // If one of received notifications is about customer changes, we should refresh customer data right away
    if (onCustomerNotifications && areCustomerNotifications(notifications)) {
      onCustomerNotifications();
    }
  }, [customerId, notificationsCenterActions, onCustomerNotifications]);

  useEffect(() => {
    // Do not pull notifications in Infinity mode, as we are not displaying them there and do not want to make unnecessary API calls
    if (isApplicationModeInfinity()) {
      return;
    }

    void pullNotifications();

    const interval = setInterval(pullNotifications, NOTIFICATIONS_POOLING_INTERVAL);
    return () => clearInterval(interval);
  }, [pullNotifications]);
};
